import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { get, pick } from 'lodash'
import queryString from 'query-string'
import {
  TextDisplay1,
  TextHeadline3,
  Link,
  TextSubHeading,
  TextCallout2,
  TextCaption2,
} from 'blocket-ui'
import { mediaQueries } from '@blazar-plugins/device'
import { buildListUrl } from 'utils/url'
import { ie11media } from 'utils/ie11'

import abroad from './icons/Abroad.svg'
import flat from './icons/Flat.svg'
import summerhouse from './icons/SummerHouse.svg'

const Wrapper = styled.nav`
  padding: 16px;

  @media ${mediaQueries.medium} {
    margin-top: 16px;
    padding: 0;
  }
`

const Headline = styled(TextDisplay1)`
  margin-bottom: 16px;

  @media ${mediaQueries.large} {
    margin-bottom: 32px;
  }
`

const SubItemHeading = styled(TextSubHeading)``

const List = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 16px;
  margin-bottom: 20px;

  ${ie11media} {
    clear: left;
    overflow: hidden;
  }

  & li {
    border-radius: 4px;
    overflow: hidden;

    ${ie11media} {
      width: calc(33.33% - 32px);
      float: left;
      padding-right: 16px;
      padding-bottom: 16px;
    }

    &.selection-list-item__pill {
      & a {
        white-space: nowrap;
        background-color: #f8f7f5;
        padding: 16px;
      }
    }

    & a {
      display: block;
      &:hover ${SubItemHeading}, &:focus ${SubItemHeading} {
        text-decoration: underline;
      }
    }
  }

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${mediaQueries.medium} {
    grid-template-columns: repeat(${(props) => props.grid}, 1fr);
    margin-bottom: 32px;
  }
`

const Description = styled(TextCallout2)`
  margin-bottom: 16px;
`

const Icon = styled.img`
  width: 100%;
  max-width: 233px;
  margin-bottom: 8px;
`

const SecondaryHeadline = styled(TextHeadline3)`
  margin-bottom: 4px;
`

const items = [
  {
    label: 'Populära kategorier',
    grid: 4,
    items: [
      {
        label: 'Hyr boende för längre tid',
        description: 'Boende i andra hand eller under längre tid',
        url:
          'https://bostad.blocket.se/hitta-bostad/?utm_source=blocket&utm_medium=web&utm_campaign=landning_page_experiment',
        external: true,
        iconUrl: flat,
      },
      {
        label: 'Hyr fritidsboende',
        description: 'Stugor och lägenheter veckovis',
        url: '/annonser/hela_sverige/bostad/fritidsboende?cg=3040&st=u',
        iconUrl: summerhouse,
      },
      {
        label: 'Utland',
        description: 'Köp eller hyr bostäder utanför Sverige',
        url: '/annonser/hela_sverige/bostad?cg=3080',
        iconUrl: abroad,
      },
    ],
  },
  {
    label: 'Välj bland alla våra kategorier',
    grid: 6,
    description:
      'Här hittar du lägenheter, villor, radhus, tomter, gårdar, fritidsboende och utland',
    items: [
      {
        label: 'Säljes',
        description: '',
        url: '/annonser/hela_sverige/bostad?cg=3000&st=s',
      },
      {
        label: 'Uthyres',
        description: '',
        url: '/annonser/hela_sverige/bostad?cg=3000&st=u',
      },
      {
        label: 'Önskar hyra',
        description: '',
        url: '/annonser/hela_sverige/bostad?cg=3000&st=h',
      },
      {
        label: 'Köpes',
        description: '',
        url: '/annonser/hela_sverige/bostad?cg=3000&st=k',
      },
      {
        label: 'Bytes',
        description: '',
        url: '/annonser/hela_sverige/bostad?cg=3000&st=b',
      },
    ],
  },
]

const mapStateToProps = (state) => ({
  searchParametersDefinition: state.searchParams.byQueryKey,
  geoPreference: pick(state.geoPicker.geoPreference, ['ag', 'r', 'm', 'sa']),
})

const SelectionList = ({ searchParametersDefinition, geoPreference }) => {
  const buildUrl = (url) => {
    const query = queryString.parse(url.split('?')[1])
    return buildListUrl({ ...query, ...geoPreference }, searchParametersDefinition)
  }

  return (
    <Wrapper>
      <Headline>Bostad</Headline>
      {items.map((item) => (
        <>
          <SecondaryHeadline>{item.label}</SecondaryHeadline>
          <Description>{item.description}</Description>
          <List grid={item.grid}>
            {item.items.map((subItem) => (
              <li
                className={
                  subItem.iconUrl ? 'selection-list-item__puff' : 'selection-list-item__pill'
                }
              >
                <Link
                  to={subItem.external ? subItem.url : buildUrl(subItem.url)}
                  external={get(subItem, 'external', false)}
                >
                  {subItem.iconUrl && <Icon src={subItem.iconUrl} />}
                  <SubItemHeading>{subItem.label}</SubItemHeading>
                  {subItem.description && <TextCaption2>{subItem.description}</TextCaption2>}
                </Link>
              </li>
            ))}
          </List>
        </>
      ))}
    </Wrapper>
  )
}

SelectionList.propTypes = {
  searchParametersDefinition: PropTypes.object.isRequired,
  geoPreference: PropTypes.object,
}

export default connect(mapStateToProps)(SelectionList)
